import React, { FC, useEffect, useState, useCallback, memo } from 'react';
import uniq from 'lodash/uniq';
import { v4 as uuidV4 } from 'uuid';
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Button from '@mui/material/Button';
import UploadIcon from '@mui/icons-material/FileUploadOutlined';
import UploadExcelDialog from 'src/views/customerGroups/components/UploadExcelDialog';
import { useCustomerUpload } from 'src/views/customerGroups/hooks/customerUpload';
import { FormFieldProps } from 'src/components/Form';
import { Customer, useGetRelated } from '../../../hooks/customerSearchQuery';
import CustomerLookUp from './CustomerLookUp';
import CustomerSearchBar from './CustomerSearchBar';
import CustomerList from './CustomerGroupMemberList';
import { NewMember } from './types';

const notFoundMap = (customerNumber: string) =>
  ({
    customerNumber,
    id: uuidV4(),
  } as Customer);

export const addStyles = {
  paste: 'paste' as const,
  search: 'search' as const,
};

export type AddStyle = keyof typeof addStyles;

export interface CustomerFilter {
  field: keyof Customer;
  value: string;
}

export type { Customer };

const CustomerGroupMembersInput: FC<FormFieldProps<any>> = ({
  error,
  helperText,
  name,
  onChange,
}) => {
  const [addStyle, setAddStyle] = useState<AddStyle>(addStyles.search);
  const [customers, setCustomers] = useState<NewMember[]>([]);

  const getRelated = useGetRelated();

  useEffect(
    () =>
      onChange(
        new CustomEvent('onChange', {
          detail: { name, value: customers.filter((c) => c.customerName) },
        })
      ),
    [customers] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const removeCustomers = useCallback(
    (ids: (string | number)[]) => {
      setCustomers(
        customers.filter(
          ({ id }) => !ids.map((i) => i.toString()).includes(id?.toString())
        )
      );
    },
    [customers, setCustomers]
  );

  const updateCustomers = useCallback(
    ({ customers: groups, not_found = [] }: any) =>
      setCustomers([
        ...not_found.map(notFoundMap),
        ...customers,
        ...groups.filter(
          (c: Customer) => !customers.find((_c) => _c.id === c.id)
        ),
      ]),
    [setCustomers, customers]
  );

  const fetchRelated = useCallback(
    (ids: string[]) => {
      const masterCustomerNumbers = uniq(
        ids
          .map((id) => customers.find((c) => c.id.toString() === id.toString()))
          .filter((c) => !!c && c.masterCustomerNumber)
          .map((c) => c?.masterCustomerNumber) as string[]
      );

      return getRelated(masterCustomerNumbers).then((customers) =>
        updateCustomers({
          customers: customers.map((c) => ({ ...c, related: true })),
        })
      );
    },
    [customers, updateCustomers] // eslint-disable-line
  );

  return (
    <Stack height="100%" overflow="hidden">
      <ShowRadioGroup
        addStyle={addStyle}
        isProductGroups={false}
        setAddStyle={setAddStyle}
        updateCustomers={updateCustomers}
      />
      <Box sx={(theme) => ({ marginBottom: theme.spacing(3) })}>
        {addStyle === addStyles.search && (
          <CustomerSearchBar
            includeCustomer={(customer) =>
              !customers
                .map(({ customerNumber }) => customerNumber)
                .includes(customer.customerNumber)
            }
            onSelected={(customer) => setCustomers([...customers, customer])}
          />
        )}
        {addStyle === addStyles.paste && (
          <CustomerLookUp onChange={updateCustomers} />
        )}
      </Box>
      <CustomerList
        addRelated={fetchRelated}
        error={error}
        helperText={helperText}
        onRemove={removeCustomers}
        rows={customers || []}
      />
    </Stack>
  );
};

export function ShowRadioGroup({
  addStyle,
  isProductGroups,
  setAddStyle,
  updateCustomers,
}: Readonly<{
  addStyle: 'paste' | 'search';
  isProductGroups: boolean;
  setAddStyle: React.Dispatch<React.SetStateAction<'paste' | 'search'>>;
  updateCustomers?: ({ customers, not_found }: any) => void;
}>) {
  const translate = useTranslate();
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  return (
    <Box
      alignItems="center"
      display="flex"
      justifyContent="space-between"
      sx={(theme) => ({ marginBottom: theme.spacing(1) })}
    >
      <RadioGroup
        aria-labelledby="demo-radio-buttons-group-label"
        name="radio-buttons-group"
        onChange={({ target: { value } }) => setAddStyle(value as AddStyle)}
        row
        value={addStyle}
      >
        <FormControlLabel
          componentsProps={{ typography: { variant: 'body2' } }}
          control={<Radio />}
          label={translate(
            isProductGroups
              ? 'ProductGroups.SearchForProducts'
              : 'CustomerGroups.searchForCustomers'
          )}
          value={addStyles.search}
        />
        {!isProductGroups && (
          <FormControlLabel
            componentsProps={{ typography: { variant: 'body2' } }}
            control={<Radio />}
            data-testid="gds-cgs-bulk-add-radio-btn"
            label={translate('CustomerGroups.bulkAddCustomersLabel')}
            value={addStyles.paste}
          />
        )}
      </RadioGroup>
      {!isProductGroups && (
        <Button
          data-testid="gds-gds-upload-excel-btn"
          onClick={() => setShowUploadDialog(true)}
          startIcon={<UploadIcon />}
          variant="newOutlined"
        >
          {translate('CustomerGroups.addCustomersFileUploadExcel')}
        </Button>
      )}
      {showUploadDialog && (
        <UploadExcelDialog
          onClose={(res) => {
            (res?.customers || res?.not_found) &&
              updateCustomers &&
              updateCustomers(res);
            setShowUploadDialog(false);
          }}
          uploadFileApi={useCustomerUpload}
        />
      )}
    </Box>
  );
}

export default memo(CustomerGroupMembersInput);
